import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { callApi, HttpMethod } from "src/services/apiService";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Grid,
  GridColumn as Column,
  GridCellProps,
  GridPageChangeEvent,
  GridSortChangeEvent,
  GridFilterChangeEvent,
} from "@progress/kendo-react-grid";
import {
  SortDescriptor,
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";
import ReactDOM from "react-dom";
import { toast } from "sonner";

interface TrustCallOneTimeScanJob {
  jobId: string;
  acctId: string | null;
  notificationUrl: string | null;
  notificationApikey: string | null;
  scanType: string | null;
  status: string;
  statusDisplay: string;
  ipAddress: string | null;
  requestCount: number;
  createdByUser: number | null;
  createDate: string;
  markedCompleteAt: string | null;
  accountName: string | null;
  createdByUserName: string | null;
}

interface TrustCallOneTimeScanResponse {
  partitionKey: string;
  rowKey: string;
  json: string | null;
  requestHeaders: string | null;
  url: string | null;
  responseStatusCode: string | null;
  responseHeaders: string | null;
  responseBody: string | null;
  exception: string | null;
  timestamp: string | null;
}

interface FilterState {
  jobId: string;
  acctId: string;
  dateFrom: Date | null;
  dateTo: Date | null;
  status: string;
  scanType: string;
}

interface StatusOption {
  value: string;
  label: string;
}

interface TrustCallDebugInfo {
  trustCallRows: Record<string, unknown>[];
  dailySnapshots: Record<string, unknown>[];
  dialerLogs: Record<string, string | number | null>[];
}

interface TrustCallOneTimeScanJobPhoneNumber {
  phoneNumber: string;
  status: string;
  statusDisplay: string;
}

// Static status options - no need to fetch from backend
const ONE_TIME_SCAN_STATUSES: StatusOption[] = [
  { value: "PENDING", label: "Pending" },
  { value: "PROCESSING", label: "Processing" },
  { value: "COMPLETED", label: "Completed" },
  { value: "NOT EXIST", label: "Not Exist" },
];

const preStyle: React.CSSProperties = {
  maxHeight: "300px",
  overflow: "auto",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const loadingPanelMarkup = (
  <div className="k-loading-mask">
    <span className="k-loading-text">Loading</span>
    <div className="k-loading-image" />
    <div className="k-loading-color" />
  </div>
);

const LoadingPanel = (props: { gridRef: React.RefObject<HTMLDivElement> }) => {
  const { gridRef } = props;
  const gridContent =
    gridRef.current && gridRef.current.querySelector(".k-grid-content");
  return gridContent
    ? ReactDOM.createPortal(loadingPanelMarkup, gridContent)
    : loadingPanelMarkup;
};

const TrustCallOneTimeScanJobs: React.FC = () => {
  const { jobId: urlJobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<TrustCallOneTimeScanJob[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState({ skip: 0, take: 50 });
  const [sort, setSort] = useState<SortDescriptor[]>([
    { field: "createDate", dir: "desc" },
  ]);
  const [gridFilter, setGridFilter] = useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [filters, setFilters] = useState<FilterState>({
    jobId: "",
    acctId: "",
    dateFrom: null,
    dateTo: null,
    status: "",
    scanType: "",
  });
  const [scanTypes, setScanTypes] = useState<string[]>([]);
  const gridRef = useRef<HTMLDivElement>(null);

  // Response modal state
  const [showJobDetailModal, setShowJobDetailModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [responses, setResponses] = useState<TrustCallOneTimeScanResponse[]>(
    [],
  );
  const [isLoadingResponses, setIsLoadingResponses] = useState(false);
  const [responseError, setResponseError] = useState("");

  // Phone numbers state
  const [phoneNumbers, setPhoneNumbers] = useState<
    TrustCallOneTimeScanJobPhoneNumber[]
  >([]);
  const [isLoadingPhoneNumbers, setIsLoadingPhoneNumbers] = useState(false);
  const [phoneNumbersError, setPhoneNumbersError] = useState("");
  const [phoneNumbersGridFilter, setPhoneNumbersGridFilter] = useState<
    CompositeFilterDescriptor | undefined
  >(undefined);

  // Expanded responses state - keyed by partitionKey+rowKey for stability
  const [expandedResponses, setExpandedResponses] = useState<Set<string>>(
    new Set(),
  );

  // Debug info modal state
  const [showDebugInfoModal, setShowDebugInfoModal] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<string | null>(
    null,
  );
  const [debugInfo, setDebugInfo] = useState<TrustCallDebugInfo | null>(null);
  const [isLoadingDebugInfo, setIsLoadingDebugInfo] = useState(false);
  const [debugInfoError, setDebugInfoError] = useState("");
  const [expandedDialerLogs, setExpandedDialerLogs] = useState<Set<number>>(
    new Set(),
  );

  const loadData = async () => {
    setIsLoading(true);
    setError("");
    try {
      const currentSort = sort.length > 0 ? sort[0] : null;
      const requestBody = {
        jobId: filters.jobId || null,
        acctId: filters.acctId || null,
        dateFrom: filters.dateFrom?.toISOString() || null,
        dateTo: filters.dateTo?.toISOString() || null,
        status: filters.status || null,
        scanType: filters.scanType || null,
        skip: page.skip,
        take: page.take,
        sortField: currentSort?.field || "createDate",
        sortDir: currentSort?.dir || "desc",
      };

      const response = await callApi(
        "sysadmin/trustcall-onetimescan-jobs",
        HttpMethod.POST,
        requestBody,
      );
      setJobs(response.data || []);
      setTotal(response.total || 0);
    } catch (err: any) {
      console.error("Error fetching TrustCall One Time Scan Jobs:", err);
      setError(`Error fetching jobs: ${err.message || err}`);
    } finally {
      setIsLoading(false);
    }
  };

  const loadScanTypes = async () => {
    try {
      const response = await callApi(
        "sysadmin/trustcall-onetimescan-jobs/scan-types",
        HttpMethod.GET,
      );
      setScanTypes(response || []);
    } catch (err) {
      console.error("Error fetching scan types:", err);
    }
  };

  useEffect(() => {
    loadScanTypes();
  }, []);

  useEffect(() => {
    loadData();
  }, [page, sort]);

  // Auto-open job view when jobId is in URL
  useEffect(() => {
    if (urlJobId) {
      setSelectedJobId(urlJobId);
      setShowJobDetailModal(true);
      loadJobResponses(urlJobId);
      loadPhoneNumbers(urlJobId);
    }
  }, [urlJobId]);

  const handleSearch = () => {
    setPage({ skip: 0, take: page.take });
    loadData();
  };

  const handleReset = () => {
    setFilters({
      jobId: "",
      acctId: "",
      dateFrom: null,
      dateTo: null,
      status: "",
      scanType: "",
    });
    setPage({ skip: 0, take: 50 });
  };

  const handlePageChange = (event: GridPageChangeEvent) => {
    setPage({ skip: event.page.skip, take: event.page.take });
  };

  const handleSortChange = (event: GridSortChangeEvent) => {
    setSort(event.sort);
  };

  const handleGridFilterChange = (event: GridFilterChangeEvent) => {
    setGridFilter(event.filter);
  };

  const loadJobResponses = async (jobId: string) => {
    setIsLoadingResponses(true);
    setResponseError("");
    setResponses([]);
    setExpandedResponses(new Set());
    try {
      const response = await callApi(
        `sysadmin/trustcall-onetimescan-jobs/${jobId}/responses`,
        HttpMethod.GET,
      );
      // Sort by timestamp descending
      const sortedResponses = (response || []).sort(
        (a: TrustCallOneTimeScanResponse, b: TrustCallOneTimeScanResponse) => {
          const dateA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
          const dateB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
          return dateB - dateA;
        },
      );
      setResponses(sortedResponses);
    } catch (err: any) {
      if (err.status === 404) {
        setResponseError("No responses found for this job.");
      } else {
        setResponseError(`Error fetching responses: ${err.message || err}`);
      }
    } finally {
      setIsLoadingResponses(false);
    }
  };

  const loadPhoneNumbers = async (jobId: string) => {
    setIsLoadingPhoneNumbers(true);
    setPhoneNumbersError("");
    setPhoneNumbers([]);
    setPhoneNumbersGridFilter(undefined);
    try {
      const response = await callApi(
        `sysadmin/trustcall-onetimescan-jobs/phone-numbers/${jobId}`,
        HttpMethod.GET,
      );
      // Helper to derive statusDisplay from status
      const getStatusDisplay = (status: string): string => {
        const found = ONE_TIME_SCAN_STATUSES.find((s) => s.value === status);
        return found?.label || status;
      };

      // Normalize response to ensure each item has the expected shape
      const normalized: TrustCallOneTimeScanJobPhoneNumber[] = (response || [])
        .map((item: unknown) => {
          if (typeof item === "string") {
            // Legacy string format - convert to object
            return {
              phoneNumber: item,
              status: "PENDING",
              statusDisplay: "Pending",
            };
          }
          if (item && typeof item === "object") {
            const obj = item as Record<string, unknown>;
            const status = String(obj.status || "PENDING");
            return {
              phoneNumber: String(obj.phoneNumber || ""),
              status,
              statusDisplay: obj.statusDisplay
                ? String(obj.statusDisplay)
                : getStatusDisplay(status),
            };
          }
          return null;
        })
        .filter(
          (item: TrustCallOneTimeScanJobPhoneNumber | null): item is TrustCallOneTimeScanJobPhoneNumber =>
            item !== null && item.phoneNumber !== "",
        );
      setPhoneNumbers(normalized);
    } catch (err: any) {
      if (err.status === 404) {
        setPhoneNumbersError("No phone numbers found for this job.");
      } else {
        setPhoneNumbersError(
          `Error fetching phone numbers: ${err.message || err}`,
        );
      }
    } finally {
      setIsLoadingPhoneNumbers(false);
    }
  };

  const loadDebugInfo = async (phoneNumber: string) => {
    setIsLoadingDebugInfo(true);
    setDebugInfoError("");
    setDebugInfo(null);
    setSelectedPhoneNumber(phoneNumber);
    setShowDebugInfoModal(true);
    try {
      const response = await callApi(
        `sysadmin/trustcall-onetimescan-jobs/debug-info?phoneNumber=${encodeURIComponent(phoneNumber)}`,
        HttpMethod.GET,
      );
      setDebugInfo(response);
    } catch (err: any) {
      setDebugInfoError(`Error fetching debug info: ${err.message || err}`);
    } finally {
      setIsLoadingDebugInfo(false);
    }
  };

  const handleCloseDebugInfoModal = () => {
    setShowDebugInfoModal(false);
    setSelectedPhoneNumber(null);
    setDebugInfo(null);
    setDebugInfoError("");
    setExpandedDialerLogs(new Set());
  };

  const toggleDialerLogExpanded = (idx: number) => {
    setExpandedDialerLogs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(idx)) {
        newSet.delete(idx);
      } else {
        newSet.add(idx);
      }
      return newSet;
    });
  };

  const formatPhoneE164 = (phone: unknown): string => {
    if (phone == null) return "-";
    const phoneStr = typeof phone === "string" ? phone : String(phone);
    // Remove non-digit characters
    const digits = phoneStr.replace(/\D/g, "");
    if (digits.length === 10) {
      return `+1${digits}`;
    } else if (digits.length === 11 && digits.startsWith("1")) {
      return `+${digits}`;
    }
    return phoneStr;
  };

  const handleViewJob = (jobId: string) => {
    setSelectedJobId(jobId);
    setShowJobDetailModal(true);
    loadJobResponses(jobId);
    loadPhoneNumbers(jobId);
    navigate(`/sysadmin/trustcall-onetimescan-jobs/${jobId}`);
  };

  const handleCloseModal = () => {
    setShowJobDetailModal(false);
    setSelectedJobId(null);
    setResponses([]);
    setResponseError("");
    setPhoneNumbers([]);
    setPhoneNumbersError("");
    setPhoneNumbersGridFilter(undefined);
    setExpandedResponses(new Set());
    navigate("/sysadmin/trustcall-onetimescan-jobs");
  };

  const getResponseKey = (response: TrustCallOneTimeScanResponse): string => {
    return `${response.partitionKey}|${response.rowKey}`;
  };

  const toggleResponseExpanded = (responseKey: string) => {
    setExpandedResponses((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(responseKey)) {
        newSet.delete(responseKey);
      } else {
        newSet.add(responseKey);
      }
      return newSet;
    });
  };

  const copyToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success(`${text} - Job ID copied to clipboard`);
        return;
      }
    } catch {
      // Fall through to fallback
    }

    // Fallback for non-secure contexts or older browsers
    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      const success = document.execCommand("copy");
      document.body.removeChild(textarea);
      if (success) {
        toast.success(`${text} - Job ID copied to clipboard`);
      } else {
        toast.error("Failed to copy to clipboard");
      }
    } catch {
      toast.error("Failed to copy to clipboard");
    }
  };

  const formatJson = (jsonString: string | null): string => {
    if (!jsonString) return "-";
    try {
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return jsonString;
    }
  };

  const formatHeaders = (headersString: string | null): string => {
    if (!headersString) return "-";
    try {
      const parsed = JSON.parse(headersString);
      if (typeof parsed === "object" && parsed !== null) {
        return Object.entries(parsed)
          .map(([key, value]) => `${key}: ${value}`)
          .join("\n");
      }
      return headersString;
    } catch {
      return headersString;
    }
  };

  const formatTimestamp = (timestamp: string | null): string => {
    if (!timestamp) return "-";
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return "-";
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");
      return `${month}/${day}/${year}, ${hours}:${minutes}:${seconds}`;
    } catch {
      return "-";
    }
  };

  // Custom cell for standard display
  const StandardCell: React.FC<GridCellProps> = (props) => {
    const { dataItem, field } = props;
    const value = field ? dataItem[field] : "";
    return <td>{value?.toString() || "-"}</td>;
  };

  // Custom cell for date formatting
  const DateCell: React.FC<GridCellProps> = (props) => {
    const { dataItem, field } = props;
    if (!dataItem || !field) return <td>-</td>;

    const value = dataItem[field];
    if (!value) return <td>-</td>;

    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return <td>-</td>;

      const formatted = date.toLocaleString("en-US", {
        dateStyle: "short",
        timeStyle: "short",
      });
      return <td>{formatted}</td>;
    } catch {
      return <td>-</td>;
    }
  };

  // Custom cell for status display
  const StatusCell: React.FC<GridCellProps> = (props) => {
    const { dataItem } = props;
    if (!dataItem) return <td>-</td>;

    const statusDisplay = dataItem.statusDisplay;
    const status = dataItem.status;

    let badgeClass = "bg-secondary";
    if (status === "PENDING") badgeClass = "bg-warning text-dark";
    if (status === "PROCESSING") badgeClass = "bg-primary";
    if (status === "COMPLETED") badgeClass = "bg-success";
    if (status === "NOT EXIST") badgeClass = "bg-danger";

    return (
      <td>
        <span className={`badge ${badgeClass}`}>{statusDisplay || "-"}</span>
      </td>
    );
  };

  // Custom cell for account display (AcctId + Name)
  const AccountCell: React.FC<GridCellProps> = (props) => {
    const { dataItem } = props;
    const acctId = dataItem.acctId;
    const accountName = dataItem.accountName;

    if (!acctId) return <td>-</td>;

    return (
      <td>
        <div>
          <strong>{acctId}</strong>
          {accountName && <div className="text-muted small">{accountName}</div>}
        </div>
      </td>
    );
  };

  // Custom cell for JobId (full GUID with copy button, truncates via CSS when narrow)
  const JobIdCell: React.FC<GridCellProps> = (props) => {
    const { dataItem } = props;
    const jobId = dataItem.jobId;

    const handleCopy = () => {
      if (jobId) {
        copyToClipboard(jobId);
      }
    };

    return (
      <td title={jobId}>
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontWeight: "bold",
              minWidth: 0,
              flex: "1 1 auto",
            }}
          >
            {jobId || "-"}
          </span>
          {jobId && (
            <button
              className="btn btn-sm btn-link p-0"
              onClick={handleCopy}
              title="Copy Job ID"
              style={{ flexShrink: 0 }}
            >
              <i className="bi bi-clipboard"></i>
            </button>
          )}
        </div>
      </td>
    );
  };

  // Custom cell for request count (clickable link to view phone numbers)
  const RequestsCell: React.FC<GridCellProps> = (props) => {
    const { dataItem } = props;
    const count = dataItem.requestCount;
    const jobId = dataItem.jobId;

    return (
      <td>
        <a
          href={`/sysadmin/trustcall-onetimescan-jobs/${jobId}`}
          onClick={(e) => {
            e.preventDefault();
            handleViewJob(jobId);
          }}
          title="View phone numbers"
          style={{ cursor: "pointer" }}
        >
          {count ?? "-"}
        </a>
      </td>
    );
  };

  // Custom cell for actions (view responses button)
  const ActionsCell: React.FC<GridCellProps> = (props) => {
    const { dataItem } = props;
    const jobId = dataItem.jobId;

    return (
      <td style={{ overflow: "visible" }}>
        <button
          className="btn btn-sm btn-outline-primary text-nowrap"
          onClick={() => handleViewJob(jobId)}
          title="View Job Details"
        >
          <i className="bi bi-eye me-1"></i>
          View
        </button>
      </td>
    );
  };

  return (
    <div className="container-fluid p-3">
      <h4 className="mb-3">TrustCall One Time Scan Jobs</h4>

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-2">
              <label className="form-label">Job ID</label>
              <input
                type="text"
                className="form-control"
                value={filters.jobId}
                onChange={(e) =>
                  setFilters({ ...filters, jobId: e.target.value })
                }
                placeholder="Filter by Job ID..."
              />
            </div>
            <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-2">
              <label className="form-label">Account ID</label>
              <input
                type="text"
                className="form-control"
                value={filters.acctId}
                onChange={(e) =>
                  setFilters({ ...filters, acctId: e.target.value })
                }
                placeholder="Filter by Account ID..."
              />
            </div>
            <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-2">
              <label className="form-label">Date From</label>
              <div>
                <DatePicker
                  selected={filters.dateFrom}
                  onChange={(date) =>
                    setFilters({ ...filters, dateFrom: date })
                  }
                  dateFormat="yyyy-MM-dd"
                  isClearable
                  className="form-control"
                  placeholderText="Start date"
                />
              </div>
            </div>
            <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-2">
              <label className="form-label">Date To</label>
              <div>
                <DatePicker
                  selected={filters.dateTo}
                  onChange={(date) => setFilters({ ...filters, dateTo: date })}
                  dateFormat="yyyy-MM-dd"
                  isClearable
                  className="form-control"
                  placeholderText="End date"
                />
              </div>
            </div>
            <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-2">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={filters.status}
                onChange={(e) =>
                  setFilters({ ...filters, status: e.target.value })
                }
              >
                <option value="">All</option>
                {ONE_TIME_SCAN_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-2">
              <label className="form-label">Scan Type</label>
              <select
                className="form-select"
                value={filters.scanType}
                onChange={(e) =>
                  setFilters({ ...filters, scanType: e.target.value })
                }
              >
                <option value="">All</option>
                {scanTypes.map((st) => (
                  <option key={st} value={st}>
                    {st.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="row mt-3">
            <div className="col">
              <button className="btn btn-primary me-2" onClick={handleSearch}>
                Search
              </button>
              <button className="btn btn-secondary" onClick={handleReset}>
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div ref={gridRef}>
        {isLoading && <LoadingPanel gridRef={gridRef} />}
        <Grid
          data={gridFilter ? filterBy(jobs, gridFilter) : jobs}
          total={gridFilter ? filterBy(jobs, gridFilter).length : total}
          pageable={{
            buttonCount: 5,
            pageSizes: [25, 50, 100],
          }}
          skip={page.skip}
          take={page.take}
          onPageChange={handlePageChange}
          sortable={true}
          sort={sort}
          onSortChange={handleSortChange}
          filterable={true}
          filter={gridFilter}
          onFilterChange={handleGridFilterChange}
          resizable={true}
          style={{ height: "calc(100vh - 280px)" }}
        >
          <Column
            field="jobId"
            title="Job ID"
            width={200}
            minResizableWidth={120}
            cell={JobIdCell}
            filter="text"
          />
          <Column
            field="acctId"
            title="Account"
            minResizableWidth={120}
            cell={AccountCell}
            filter="text"
          />
          <Column
            field="scanType"
            title="Scan Type"
            width={150}
            minResizableWidth={120}
            cell={StandardCell}
            filter="text"
          />
          <Column
            field="statusDisplay"
            title="Status"
            width={140}
            minResizableWidth={120}
            cell={StatusCell}
            filter="text"
          />
          <Column
            field="requestCount"
            title="Requests"
            minResizableWidth={70}
            cell={RequestsCell}
            filter="numeric"
          />
          <Column
            field="ipAddress"
            title="IP Address"
            minResizableWidth={100}
            cell={StandardCell}
            filter="text"
          />
          <Column
            field="createdByUserName"
            title="Created By"
            minResizableWidth={100}
            cell={StandardCell}
            filter="text"
          />
          <Column
            field="createDate"
            title="Created Date"
            minResizableWidth={120}
            cell={DateCell}
            filter="date"
          />
          <Column
            field="markedCompleteAt"
            title="Completed At"
            minResizableWidth={120}
            cell={DateCell}
            filter="date"
          />
          <Column
            field="notificationUrl"
            title="Notification URL"
            width={250}
            minResizableWidth={180}
            cell={StandardCell}
            filter="text"
          />
          <Column
            title="Actions"
            width={120}
            minResizableWidth={120}
            cell={ActionsCell}
            filterable={false}
            sortable={false}
            locked={true}
          />
        </Grid>
      </div>

      {/* Job Detail Modal */}
      {showJobDetailModal && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Job Details
                  {selectedJobId && (
                    <small className="text-muted ms-2">
                      ({selectedJobId})
                      <button
                        className="btn btn-sm btn-link p-0 ms-1"
                        onClick={() => copyToClipboard(selectedJobId)}
                        title="Copy Job ID"
                      >
                        <i className="bi bi-clipboard"></i>
                      </button>
                    </small>
                  )}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                ></button>
              </div>
              <div className="modal-body">
                {/* Phone Numbers Section */}
                <h6 className="mb-3">Phone Numbers</h6>
                {isLoadingPhoneNumbers && (
                  <div className="text-center py-3">
                    <div
                      className="spinner-border text-primary spinner-border-sm"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <span className="ms-2">Loading phone numbers...</span>
                  </div>
                )}

                {phoneNumbersError && (
                  <div className="alert alert-warning">{phoneNumbersError}</div>
                )}

                {!isLoadingPhoneNumbers &&
                  !phoneNumbersError &&
                  phoneNumbers.length === 0 && (
                    <div className="alert alert-info">
                      No phone numbers found for this job.
                    </div>
                  )}

                {phoneNumbers.length > 0 && (
                  <div className="mb-4">
                    <Grid
                      data={
                        phoneNumbersGridFilter
                          ? filterBy(phoneNumbers, phoneNumbersGridFilter)
                          : phoneNumbers
                      }
                      filterable={true}
                      filter={phoneNumbersGridFilter}
                      onFilterChange={(e: GridFilterChangeEvent) => {
                        setPhoneNumbersGridFilter(e.filter);
                      }}
                      resizable={true}
                      style={
                        phoneNumbers.length > 5
                          ? { height: "300px" }
                          : undefined
                      }
                    >
                      <Column
                        field="phoneNumber"
                        title="Phone Number"
                        filter="text"
                      />
                      <Column
                        field="statusDisplay"
                        title="Status"
                        filter="text"
                        cell={StatusCell}
                      />
                      <Column
                        title="Actions"
                        filterable={false}
                        cell={(props: GridCellProps) => (
                          <td>
                            <button
                              className="btn btn-sm btn-outline-info"
                              onClick={() =>
                                loadDebugInfo(props.dataItem.phoneNumber)
                              }
                              title="View Debug Info"
                            >
                              <i className="bi bi-search me-1"></i>
                              Debug Info
                            </button>
                          </td>
                        )}
                      />
                    </Grid>
                  </div>
                )}

                {/* API Responses Section */}
                <h6 className="mb-3">API Responses</h6>
                {isLoadingResponses && (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading responses...</p>
                  </div>
                )}

                {responseError && (
                  <div className="alert alert-warning">{responseError}</div>
                )}

                {!isLoadingResponses &&
                  !responseError &&
                  responses.length === 0 && (
                    <div className="alert alert-info">
                      No responses found for this job.
                    </div>
                  )}

                {responses.map((response) => {
                  const responseKey = getResponseKey(response);
                  const isExpanded = expandedResponses.has(responseKey);
                  return (
                    <div key={responseKey} className="card mb-3">
                      <button
                        type="button"
                        className="card-header d-flex justify-content-between align-items-center w-100 border-0 text-start"
                        style={{ cursor: "pointer" }}
                        onClick={() => toggleResponseExpanded(responseKey)}
                        aria-expanded={isExpanded}
                        aria-controls={`response-body-${responseKey}`}
                      >
                        <span>
                          <i
                            className={`bi ${isExpanded ? "bi-chevron-down" : "bi-chevron-right"} me-2`}
                          ></i>
                          <strong>Attempt:</strong> {response.rowKey}
                          {response.timestamp && (
                            <span className="text-muted ms-3">
                              {formatTimestamp(response.timestamp)}
                            </span>
                          )}
                        </span>
                        <span
                          className={`badge ${
                            response.responseStatusCode?.startsWith("2")
                              ? "bg-success"
                              : "bg-danger"
                          }`}
                        >
                          {response.responseStatusCode || "N/A"}
                        </span>
                      </button>
                      {isExpanded && (
                        <div
                          id={`response-body-${responseKey}`}
                          className="card-body"
                        >
                          <div className="row">
                            <div className="col-12 mb-3">
                              <strong>URL:</strong>
                              <div className="text-break">
                                {response.url || "-"}
                              </div>
                            </div>
                          </div>

                          <ul className="nav nav-tabs" role="tablist">
                            <li className="nav-item">
                              <button
                                className="nav-link active"
                                data-bs-toggle="tab"
                                data-bs-target={`#json-${responseKey}`}
                                type="button"
                              >
                                Payload JSON
                              </button>
                            </li>
                            <li className="nav-item">
                              <button
                                className="nav-link"
                                data-bs-toggle="tab"
                                data-bs-target={`#reqHeaders-${responseKey}`}
                                type="button"
                              >
                                Payload Headers
                              </button>
                            </li>
                            <li className="nav-item">
                              <button
                                className="nav-link"
                                data-bs-toggle="tab"
                                data-bs-target={`#responseBody-${responseKey}`}
                                type="button"
                              >
                                Response Body
                              </button>
                            </li>
                            <li className="nav-item">
                              <button
                                className="nav-link"
                                data-bs-toggle="tab"
                                data-bs-target={`#resHeaders-${responseKey}`}
                                type="button"
                              >
                                Response Headers
                              </button>
                            </li>
                            {response.exception && (
                              <li className="nav-item">
                                <button
                                  className="nav-link text-danger"
                                  data-bs-toggle="tab"
                                  data-bs-target={`#exception-${responseKey}`}
                                  type="button"
                                >
                                  Exception
                                </button>
                              </li>
                            )}
                          </ul>
                          <div className="tab-content border border-top-0 p-3">
                            <div
                              className="tab-pane fade show active"
                              id={`json-${responseKey}`}
                            >
                              <pre
                                className="bg-light p-2 rounded"
                                style={preStyle}
                              >
                                {formatJson(response.json)}
                              </pre>
                            </div>
                            <div
                              className="tab-pane fade"
                              id={`reqHeaders-${responseKey}`}
                            >
                              <pre
                                className="bg-light p-2 rounded"
                                style={preStyle}
                              >
                                {formatJson(response.requestHeaders)}
                              </pre>
                            </div>
                            <div
                              className="tab-pane fade"
                              id={`responseBody-${responseKey}`}
                            >
                              <pre
                                className="bg-light p-2 rounded"
                                style={preStyle}
                              >
                                {formatJson(response.responseBody)}
                              </pre>
                            </div>
                            <div
                              className="tab-pane fade"
                              id={`resHeaders-${responseKey}`}
                            >
                              <pre
                                className="bg-light p-2 rounded"
                                style={preStyle}
                              >
                                {formatHeaders(response.responseHeaders)}
                              </pre>
                            </div>
                            {response.exception && (
                              <div
                                className="tab-pane fade"
                                id={`exception-${responseKey}`}
                              >
                                <pre
                                  className="bg-danger bg-opacity-10 text-danger p-2 rounded"
                                  style={preStyle}
                                >
                                  {formatJson(response.exception)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Debug Info Modal */}
      {showDebugInfoModal && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1060 }}
        >
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Debug Info
                  {selectedPhoneNumber && (
                    <span className="badge bg-secondary ms-2">
                      {selectedPhoneNumber}
                    </span>
                  )}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseDebugInfoModal}
                ></button>
              </div>
              <div className="modal-body">
                {isLoadingDebugInfo && (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading debug info...</p>
                  </div>
                )}

                {debugInfoError && (
                  <div className="alert alert-warning">{debugInfoError}</div>
                )}

                {debugInfo && !isLoadingDebugInfo && (
                  <>
                    {/* Dialer Logs */}
                    {debugInfo.dialerLogs.length === 0 ? (
                      <div className="alert alert-info mb-3">
                        No dialer logs found.
                      </div>
                    ) : (
                      <div
                        className="table-responsive mb-4"
                        style={{ maxHeight: "400px", overflow: "auto" }}
                      >
                        <table className="table table-sm table-striped table-bordered align-middle">
                          <thead
                            className="table-light"
                            style={{ position: "sticky", top: 0 }}
                          >
                            <tr>
                              <th>Log ID</th>
                              <th>Initiated</th>
                              <th>Duration</th>
                              <th>Array Device</th>
                              <th>Queue</th>
                              <th>Queued</th>
                              <th>Completed</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {debugInfo.dialerLogs.map((row, idx) => {
                              const isExpanded = expandedDialerLogs.has(idx);
                              return (
                                <React.Fragment key={idx}>
                                  <tr>
                                    <td>{row.logId ?? "-"}</td>
                                    <td>
                                      {row.initiatedCallOn
                                        ? formatTimestamp(
                                            String(row.initiatedCallOn),
                                          )
                                        : "-"}
                                    </td>
                                    <td>{row.callDurationSecs ?? "-"}</td>
                                    <td>{formatPhoneE164(row.numberToDial)}</td>
                                    <td>{row.queueName ?? "-"}</td>
                                    <td>
                                      {row.timeQueued
                                        ? formatTimestamp(
                                            String(row.timeQueued),
                                          )
                                        : "-"}
                                    </td>
                                    <td>
                                      {row.callCompletedAt
                                        ? formatTimestamp(
                                            String(row.callCompletedAt),
                                          )
                                        : "-"}
                                    </td>
                                    <td>
                                      <button
                                        className="btn btn-link btn-sm text-muted p-0"
                                        onClick={() =>
                                          toggleDialerLogExpanded(idx)
                                        }
                                        style={{ fontSize: "11px" }}
                                        aria-expanded={isExpanded}
                                        aria-controls={`dialerLog-json-${idx}`}
                                      >
                                        JSON
                                      </button>
                                    </td>
                                  </tr>
                                  {isExpanded && (
                                    <tr id={`dialerLog-json-${idx}`}>
                                      <td colSpan={8} className="p-0">
                                        <pre
                                          className="bg-light p-2 m-0"
                                          style={{
                                            maxHeight: "200px",
                                            overflow: "auto",
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                            fontSize: "12px",
                                          }}
                                        >
                                          {JSON.stringify(row, null, 2)}
                                        </pre>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseDebugInfoModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrustCallOneTimeScanJobs;
